import { useState, useEffect, useRef, useCallback, useMemo, CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search,
  X,
  Building,
  MapPin,
  Info,
  AlertTriangle,
  Phone,
  Navigation,
  Sparkles,
  Route,
  Check,
  UserPlus,
  ArrowLeft,
} from 'lucide-react';
import { InspectionCard } from '../components/inspection-card';
import { useAuth } from 'react-oidc-context';
import { useTranslationWithLanguage } from '../context/language-context';
import { createPortal } from 'react-dom';
import SuggestedInspectionsPage from './SuggestedInspections';
import { Stack } from '@phosphor-icons/react';
import { set } from 'date-fns';
import { showToast } from '@/utils/helpers';
import { Header } from '@/components/header';

// Define types for establishments
interface Establishment {
  id: number;
  summary: string;
  description: string;
  emirate_id: number;
  emirate: { id: number; name_en: string; name_ar: string; };
  establishment_id: string;
  establishment: { id: string; name_en: string; name_ar: string; };
  assigned_to: string;
  assigned_by?: string;
  assigned_to_user: { id: string; username: string; email: string; first_name: string; last_name: string; };
  inspection_type_id: number;
  inspection_type: { id: number; name_en: string; name_ar: string; };
  risk_score: number;
  risk_grade: string;
  risk_category?: string;
  hours: string;
  location: { lat: number; lng: number; };
  status: string;
  task_status_id?: number;
  distance?: number;
  groupCount?: number;
  arrivalTime?: Date;
  groupedItems?: Establishment[];
}

// Base URL from environment variables
const BASE_URL = import.meta.env.VITE_BASE_URL;

// --- 2GIS MapGL Configuration ---
const urbiApiKey = '1230123-12312-123';
const mapContainerStyle: CSSProperties = {
  width: '100%',
  height: '100vh',
};
const defaultCenter = { lat: 25.197197, lng: 55.274376 };
// --- End 2GIS MapGL Configuration ---

// Define types for 2GIS MapGL
type LngLatTuple = [number, number];
type MapInstance = any;
type MarkerInstance = any;
type PolylineInstance = any;
type DirectionsStep = any;

// Helper function to calculate risk grade
const calculateRiskGrade = (item: any): string => {
  if (item.params?.risk_category) return item.params?.risk_category;
  return 'HIGH';
};

// Helper function to map API status to component status
const mapStatus = (statusDisplay: string, taskStatusId?: number): string => {
  if (taskStatusId === 6) return 'assigned';
  switch (statusDisplay?.toUpperCase()) {
    case 'ASSIGNED INSPECTOR': case 'ASSIGNED': case 'ASSIGNED TO INSPECTOR': case 'INSPECTOR ASSIGNED': return 'assigned';
    case 'INPROGRESS': case 'IN PROGRESS': case 'ACTIVE': return 'inProgress';
    case 'PAUSED': return 'paused';
    case 'COMPLETED': case 'DONE': case 'FINISHED': return 'completed';
    case 'POOL': case 'UNASSIGNED': return 'pool';
    default: return taskStatusId === 6 ? 'assigned' : 'assigned';
  }
};

export default function MapUrbiPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t, language } = useTranslationWithLanguage();
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapInstance | null>(null);
  const establishmentMarkersRef = useRef<MarkerInstance[]>([]);
  const userLocationMarkerRef = useRef<MarkerInstance | null>(null);
  const routePolylineRef = useRef<PolylineInstance | null>(null);
  const routeMarkersRef = useRef<MarkerInstance[]>([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedEstablishment, setSelectedEstablishment] = useState<string | null>(null);
  const [mapError, setMapError] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [isBottomSheetExpanded, setIsBottomSheetExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  type BottomSheetContent = 'inspections' | 'suggestions' | 'routes';
  const [bottomSheetContent, setBottomSheetContent] = useState<BottomSheetContent>('inspections');
  const [bottomSheetHeight, setBottomSheetHeight] = useState('50%');
  const [activeFilter, setActiveFilter] = useState('adhoc');
  const [currentLocation, setCurrentLocation] = useState<LngLatTuple | null>(null);
  const [isAdhoc, setIsAdHoc] = useState(false);

  // Route optimization states
  const [optimizedRoute, setOptimizedRoute] = useState<Establishment[]>([]);
  const [routeInfo, setRouteInfo] = useState<{ totalDistance: number; totalTime: number; } | null>(null);
  const [navigationActive, setNavigationActive] = useState(false);
  const [navigationSteps, setNavigationSteps] = useState<DirectionsStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Bulk assignment states
  const [selectedInspections, setSelectedInspections] = useState<string[]>([]);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [bulkAssignResults, setBulkAssignResults] = useState({ success: 0, failed: 0, total: 0 });
  const [showBulkAssignToast, setShowBulkAssignToast] = useState(false);
  const [bulkAssignToastType, setBulkAssignToastType] = useState<'success' | 'error'>('success');
  const [bulkAssignToastMessage, setBulkAssignToastMessage] = useState('');
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(5);
  const [totalSelectedPoints, setTotalSelectedPoints] = useState(0);

  // State to store counts for each filter type
  const [filterCounts, setFilterCounts] = useState<Record<string, number>>({
    pool: 0, adhoc: 0, assigned: 0, inProgress: 0, paused: 0, completed: 0,
    labourAccommodation: 0, complaints: 0, orders: 0
  });

  // Filter buttons configuration
  const filterButtons = [
    { id: 'adHoc', icon: <AlertTriangle className="w-3.5 h-3.5" />, action: 'setFilter', filterValue: 'adhoc', contentType: null, count: filterCounts.adhoc },
    { id: 'myTasks', icon: <MapPin className="w-3.5 h-3.5" />, action: 'setFilter', filterValue: 'assigned', contentType: null, count: filterCounts.assigned },
    { id: 'pool', icon: <Stack className="w-3.5 h-3.5" />, action: 'setFilter', filterValue: 'pool', contentType: null, count: filterCounts.pool },
    { id: 'labourAccommodation', icon: <Building className="w-3.5 h-3.5" />, action: 'setFilter', filterValue: 'labourAccommodation', contentType: null, count: filterCounts.labourAccommodation },
    { id: 'complaints', icon: <Phone className="w-3.5 h-3.5" />, action: 'setFilter', filterValue: 'complaints', contentType: null, count: filterCounts.complaints },
    { id: 'orders', icon: <Stack className="w-3.5 h-3.5" />, action: 'setFilter', filterValue: 'orders', contentType: null, count: filterCounts.orders },
    { id: 'active', icon: <Route className="w-3.5 h-3.5" />, action: 'setFilter', filterValue: 'inProgress', contentType: null, count: filterCounts.inProgress },
    { id: 'paused', icon: <AlertTriangle className="w-3.5 h-3.5" />, action: 'setFilter', filterValue: 'paused', contentType: null, count: filterCounts.paused },
    { id: 'completed', icon: <Info className="w-3.5 h-3.5" />, action: 'setFilter', filterValue: 'completed', contentType: null, count: filterCounts.completed }
  ];

  // State to hold all establishments
  const [establishments, setEstablishments] = useState<Establishment[]>([]);

  // Hide webkit scrollbars
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `.hide-webkit-scrollbar::-webkit-scrollbar { display: none; }`;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // Dispatch custom event when bottom sheet state changes
  useEffect(() => {
    const event = new CustomEvent('bottomSheetStateChange', { detail: { isExpanded: isBottomSheetExpanded } });
    window.dispatchEvent(event);
  }, [isBottomSheetExpanded]);

  // Load 2GIS MapGL script and initialize map
  useEffect(() => {
    let mapInstance: MapInstance | null = null;
    const scriptId = '2gis-mapgl-script';

    if (!document.getElementById(scriptId) && mapContainer.current) {
      console.log('Loading 2GIS MapGL script...');
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://mapgl.2gis.com/api/2.0';
      script.async = true;
      script.onload = () => {
        console.log('2GIS MapGL script loaded');
        try {
          const mapgl = (window as any).mapgl;
          if (!mapgl) throw new Error('2GIS MapGL not available');

          mapInstance = new mapgl.Map(mapContainer.current!, {
            center: [defaultCenter.lng, defaultCenter.lat],
            zoom: 10,
            key: urbiApiKey,
          });
          mapRef.current = mapInstance;
          setMapLoading(false);
          console.log('2GIS MapGL initialized');

          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const { latitude, longitude } = position.coords;
                const currentPos: LngLatTuple = [longitude, latitude];
                setCurrentLocation(currentPos);
                mapInstance.setCenter(currentPos);
                mapInstance.setZoom(16);
              },
              (error) => {
                console.error('Error getting initial location:', error);
                setMapError(true);
              }
            );
          } else {
            console.warn('Geolocation not supported.');
            setMapError(true);
          }
        } catch (mapInitError) {
          console.error('Failed to initialize 2GIS MapGL:', mapInitError);
          setMapError(true);
          setMapLoading(false);
        }
      };
      script.onerror = () => {
        console.error('Failed to load 2GIS MapGL script');
        setMapError(true);
        setMapLoading(false);
      };
      document.head.appendChild(script);
    }

    return () => {
      if (mapInstance) {
        mapInstance.destroy();
      }
      mapRef.current = null;
      const script = document.getElementById(scriptId);
      if (script) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Fetch establishments data
  const fetchEstablishmentsData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let url = `${BASE_URL}/jsonapi/transform/task_list?sortBy=created_at&sortOrder=desc&page=1&pageSize=100`;
      let conditionParam = {};
      if (activeFilter === 'pool') conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '2' }] };
      else if (activeFilter === 'adhoc') conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '6' }, {"field":"assigned_by","operator":"ne","value":"self"}] };
      else if (activeFilter === 'assigned') conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '6' }, {"field":"assigned_by","operator":"eq","value":"self"}] };
      else if (activeFilter === 'inProgress') conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '14' }] };
      else if (activeFilter === 'paused') conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '22' }] };
      else if (activeFilter === 'completed') {
        conditionParam = { rules: [{ operator: 'eq', field: 'task_status_id', value: '15' }] };
        url = `${BASE_URL}/jsonapi/transform/completed_tasks?sortBy=created_at&sortOrder=desc&page=1&pageSize=100`;
      }
      else if (activeFilter === 'labourAccommodation') conditionParam = { rules: [{ operator: 'eq', field: 'task_type', value: 'labour_accommodation' }] };
      else if (activeFilter === 'complaints') conditionParam = { rules: [{ operator: 'eq', field: 'task_type', value: 'complaint' }] };
      else if (activeFilter === 'orders') conditionParam = { rules: [{ operator: 'eq', field: 'task_type', value: 'order' }] };

      let globalFilterParam = {};
      if (debouncedSearchQuery) globalFilterParam = { searchTerm: debouncedSearchQuery, searchFields: ['establishment_name_en', 'id', 'establishment_name_ar', 'establishment_id'] };
      if (Object.keys(conditionParam).length > 0) url += `&condition=${encodeURIComponent(JSON.stringify(conditionParam))}`;
      if (Object.keys(globalFilterParam).length > 0) url += `&globalFilter=${encodeURIComponent(JSON.stringify(globalFilterParam))}`;

      const response = await fetch(url, { headers: { Authorization: `Bearer ${auth?.user?.access_token}` } });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (activeFilter === 'adhoc' && data.data.length > 0) setIsAdHoc(true); else if (activeFilter === 'adhoc') setIsAdHoc(false);

      const mappedEstablishments = data.data.map((item: any) => ({
        id: item.task_id,
        summary: `Routine inspection with risk: ${item.params?.risk_category}`,
        description: item.params?.message || 'No description available',
        emirate_id: item.emirate_id,
        emirate: { id: item.emirate_id, name_en: item.emirate_display || '', name_ar: item.emirate_display || '' },
        establishment_id: item.establishment_id,
        establishment: { id: item.establishment_id, name_en: item.establishment?.name_en || '', name_ar: item.establishment?.name_ar || '' },
        assigned_to: item.assigned_to || '',
        assigned_by: item.assigned_by || '',
        assigned_to_user: { id: item.assigned_to || '', username: item.assigned_to || 'Unknown', email: '', first_name: '', last_name: '' },
        inspection_type_id: item.params?.inspection_type_id ? parseInt(item.params.inspection_type_id) : 0,
        inspection_type: { id: item.params?.inspection_type_id ? parseInt(item.params.inspection_type_id) : 0, name_en: item.params?.inspection_type_display || '', name_ar: item.params?.inspection_type_display || '' },
        risk_score: item.risk_score || 0,
        risk_grade: calculateRiskGrade(item),
        risk_category: item.params?.risk_category || '',
        status: mapStatus(item.task_status_display || '', item.task_status_id),
        task_status_id: item.task_status_id,
        hours: '',
        location: { lat: item.establishment?.latitude ? parseFloat(item.establishment.latitude) : 0, lng: item.establishment?.longitude ? parseFloat(item.establishment.longitude) : 0 },
      }));
      setEstablishments(mappedEstablishments);

      const fetchAllCounts = async () => {
        const counts: Record<string, number> = { pool: 0, adhoc: 0, assigned: 0, inProgress: 0, paused: 0, completed: 0, labourAccommodation: 0, complaints: 0, orders: 0 };
        const fetchCount = async (condition: any, endpoint = 'task_list') => {
          try {
            const res = await fetch(`${BASE_URL}/jsonapi/transform/${endpoint}?condition=${encodeURIComponent(JSON.stringify(condition))}&page=1&pageSize=1`, {
              headers: { Authorization: `Bearer ${auth?.user?.access_token}` }
            });
            if (res.ok) {
              const d = await res.json();
              return d?.total || 0;
            }
          } catch (err) {
            console.error(`Error fetching count:`, err);
          }
          return 0;
        };
        counts.pool = await fetchCount({ rules: [{ operator: 'eq', field: 'task_status_id', value: '2' }] });
        counts.adhoc = await fetchCount({ rules: [{ operator: 'eq', field: 'task_status_id', value: '6' }, { field: "assigned_by", operator: "ne", value: "self" }] });
        counts.assigned = await fetchCount({ rules: [{ operator: 'eq', field: 'task_status_id', value: '6' }, { field: "assigned_by", operator: "eq", value: "self" }] });
        counts.inProgress = await fetchCount({ rules: [{ operator: 'eq', field: 'task_status_id', value: '14' }] });
        counts.paused = await fetchCount({ rules: [{ operator: 'eq', field: 'task_status_id', value: '22' }] });
        counts.completed = await fetchCount({ rules: [{ operator: 'eq', field: 'task_status_id', value: '15' }] }, 'completed_tasks');
        setFilterCounts(counts);
      };
      await fetchAllCounts();
    } catch (err) {
      console.error('Failed to fetch establishments:', err);
      setError('Failed to fetch establishments. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [activeFilter, debouncedSearchQuery, auth?.user?.access_token]);

  useEffect(() => {
    fetchEstablishmentsData().catch((error) => console.error('Failed to fetch establishments:', error));
  }, [activeFilter, debouncedSearchQuery, fetchEstablishmentsData]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearchQuery(searchQuery), 1500);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery]);

  useEffect(() => {
    try {
      const assignedInspectionsJson = localStorage.getItem('assignedInspections');
      if (assignedInspectionsJson) {
        const assignedInspections = JSON.parse(assignedInspectionsJson);
        if (Array.isArray(assignedInspections) && assignedInspections.length > 0) {
          const existingIds = new Set(establishments.map((e) => e.id));
          const newAssignedInspections = assignedInspections.filter((inspection: Establishment) => !existingIds.has(inspection.id));
          const formattedAssignedInspections = newAssignedInspections.map((inspection: Establishment) => ({ ...inspection, status: 'assigned' }));
          if (formattedAssignedInspections.length > 0) {
            setEstablishments((prev) => [...prev, ...formattedAssignedInspections]);
          }
        }
      }
    } catch (error) {
      console.error('Error loading assigned inspections from localStorage:', error);
    }
  }, [establishments]);

  // Group establishments
  const groupedEstablishments = useMemo(() => {
    const groups: Record<string, Establishment[]> = {};
    establishments.forEach(item => {
      const key = item.establishment_id;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return Object.entries(groups).map(([_, items]) => {
      const baseItem = items[0];
      return {
        ...baseItem,
        id: items.map(i => i.id).join('_'),
        groupedItems: items,
        groupCount: items.length,
        inspection_type: {
          ...baseItem.inspection_type,
          name_en: items.length > 1 ? `${baseItem.inspection_type.name_en} + ${items.length - 1}` : baseItem.inspection_type.name_en,
          name_ar: items.length > 1 ? `${baseItem.inspection_type.name_ar} + ${items.length - 1}` : baseItem.inspection_type.name_ar,
        }
      };
    });
  }, [establishments]);

  // Effect to add/update establishment markers
  useEffect(() => {
    if (!mapRef.current || mapLoading || !window.mapgl) return;
    const map = mapRef.current;
    const mapgl = (window as any).mapgl;

    establishmentMarkersRef.current.forEach(marker => marker.destroy());
    establishmentMarkersRef.current = [];

    establishments.forEach((item) => {
      if (item.location.lat && item.location.lng) {
        try {
          const marker = new mapgl.Marker(map, {
            coordinates: [item.location.lng, item.location.lat],
            html: `<div style="width: 24px; height: 24px; border-radius: 50%; background-color: ${getRiskColorHex(item.risk_grade)}; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; border: 1px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3); cursor: pointer;">!</div>`
          });
          marker.on('click', () => {
            const groupItems = groupedEstablishments.find(g => g.establishment_id === item.establishment_id);
            const groupId = groupItems ? groupItems.id : String(item.id);
            setSelectedEstablishment(groupId);
            if (!isBottomSheetExpanded) setIsBottomSheetExpanded(true);
            map.setCenter([item.location.lng, item.location.lat], { animate: true, duration: 300 });
            map.setZoom(15, { animate: true, duration: 300 });
          });
          establishmentMarkersRef.current.push(marker);
        } catch (markerError) {
          console.error("Error creating establishment marker:", item.id, markerError);
        }
      }
    });
  }, [establishments, mapLoading, groupedEstablishments, isBottomSheetExpanded]);

  // Effect to add/update current location marker
  useEffect(() => {
    if (!mapRef.current || !currentLocation || mapLoading || !window.mapgl) return;
    const map = mapRef.current;
    const mapgl = (window as any).mapgl;

    if (userLocationMarkerRef.current) {
      userLocationMarkerRef.current.setCoordinates(currentLocation);
    } else {
      try {
        userLocationMarkerRef.current = new mapgl.Marker(map, {
          coordinates: currentLocation,
          html: '<div style="width: 16px; height: 16px; border-radius: 50%; background-color: #4285F4; border: 2px solid white; box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.3);"></div>',
          zIndex: 999
        });
      } catch (markerError) {
        console.error("Error creating user location marker:", markerError);
      }
    }
  }, [currentLocation, mapLoading]);

  // Effect to draw/update route
  useEffect(() => {
    if (!mapRef.current || mapLoading || !window.mapgl) return;
    const map = mapRef.current;
    const mapgl = (window as any).mapgl;

    if (routePolylineRef.current) routePolylineRef.current.destroy();
    routeMarkersRef.current.forEach(marker => marker.destroy());
    routePolylineRef.current = null;
    routeMarkersRef.current = [];

    if (optimizedRoute.length > 0 && routeInfo) {
      const routeCoords: LngLatTuple[] = [
        currentLocation || [defaultCenter.lng, defaultCenter.lat],
        ...optimizedRoute.map(item => [item.location.lng, item.location.lat] as LngLatTuple)
      ];

      try {
        routePolylineRef.current = new mapgl.Polyline(map, {
          coordinates: routeCoords,
          color: '#ab761b',
          width: 4
        });
        const newRouteMarkers: MarkerInstance[] = [];
        optimizedRoute.forEach((establishment, index) => {
          const marker = new mapgl.Marker(map, {
            coordinates: [establishment.location.lng, establishment.location.lat],
            label: { text: (index + 1).toString(), color: '#ffffff', fontSize: 12, haloColor: '#ab761b', haloRadius: 5 },
            zIndex: 100 + index
          });
          newRouteMarkers.push(marker);
        });
        routeMarkersRef.current = newRouteMarkers;
      } catch (routeError) {
        console.error("Error drawing route:", routeError);
      }
    }
  }, [optimizedRoute, routeInfo, currentLocation, mapLoading]);

  const toggleBottomSheet = () => setIsBottomSheetExpanded(!isBottomSheetExpanded);
  const showSuggestions = () => { setBottomSheetContent('suggestions'); setBottomSheetHeight('50%'); setIsBottomSheetExpanded(true); };
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };
  const showRouteOptimization = () => {
    setBottomSheetContent('routes');
    setBottomSheetHeight('50%');
    setIsBottomSheetExpanded(true);
    if (currentLocation) calculateOptimalRoute(currentLocation);
    else navigator.geolocation.getCurrentPosition(
      (pos) => {
        const userLoc: LngLatTuple = [pos.coords.longitude, pos.coords.latitude];
        setCurrentLocation(userLoc);
        calculateOptimalRoute(userLoc);
      },
      (err) => {
        console.error('Error getting location:', err);
        calculateOptimalRoute();
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  };
  const recalculateRoute = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const userLoc: LngLatTuple = [pos.coords.longitude, pos.coords.latitude];
        setCurrentLocation(userLoc);
        calculateOptimalRoute(userLoc);
      },
      (err) => {
        console.error('Error getting location:', err);
        calculateOptimalRoute(currentLocation || undefined);
      }
    );
  };

  const calculateOptimalRoute = async (startLoc?: LngLatTuple) => {
    const assignedTasks = establishments.filter((item) => item.status === 'assigned');
    if (assignedTasks.length === 0) { setOptimizedRoute([]); setRouteInfo(null); return; }
    let startCoords: LngLatTuple = startLoc || [defaultCenter.lng, defaultCenter.lat];
    if (!startLoc && currentLocation) startCoords = currentLocation;
    else if (!startLoc) {
      try {
        const pos = await new Promise<GeolocationPosition>((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }));
        const freshCoords: LngLatTuple = [pos.coords.longitude, pos.coords.latitude];
        setCurrentLocation(freshCoords);
        startCoords = freshCoords;
      } catch (err) {
        console.error('Error getting location:', err);
        startCoords = currentLocation || [defaultCenter.lng, defaultCenter.lat];
      }
    }
    const establishmentGroups: Record<string, Establishment[]> = {};
    assignedTasks.forEach(task => {
      if (!establishmentGroups[task.establishment_id]) establishmentGroups[task.establishment_id] = [];
      establishmentGroups[task.establishment_id].push(task);
    });
    let currentCoords: LngLatTuple = [...startCoords];
    const visitedEstablishmentIds = new Set<string>();
    const newOptimizedRoute: Establishment[] = [];
    const allEstablishmentGroups = Object.values(establishmentGroups);
    while (visitedEstablishmentIds.size < allEstablishmentGroups.length) {
      let nearestEstablishment: Establishment | null = null;
      let minDistance = Infinity;
      let nearestGroupIndex = -1;
      allEstablishmentGroups.forEach((group, index) => {
        const establishmentId = group[0].establishment_id;
        if (visitedEstablishmentIds.has(establishmentId)) return;
        const establishment = group[0];
        const distance = calculateDistance(currentCoords[1], currentCoords[0], establishment.location.lat, establishment.location.lng);
        if (distance < minDistance) {
          minDistance = distance;
          nearestEstablishment = establishment;
          nearestGroupIndex = index;
        }
      });
      if (nearestEstablishment !== null && nearestGroupIndex !== -1) {
        const establishment = nearestEstablishment as Establishment;
        const establishmentId = establishment.establishment_id;
        const group = allEstablishmentGroups[nearestGroupIndex];
        visitedEstablishmentIds.add(establishmentId);
        const establishmentWithDistance = { ...establishment, distance: minDistance, groupCount: group.length };
        newOptimizedRoute.push(establishmentWithDistance);
        currentCoords = [establishment.location.lng, establishment.location.lat];
      } else break;
    }
    let totalDistance = 0;
    const routeCoordsForCalc: LngLatTuple[] = [startCoords, ...newOptimizedRoute.map(e => [e.location.lng, e.location.lat] as LngLatTuple)];
    for (let i = 1; i < routeCoordsForCalc.length; i++) {
      totalDistance += calculateDistance(routeCoordsForCalc[i-1][1], routeCoordsForCalc[i-1][0], routeCoordsForCalc[i][1], routeCoordsForCalc[i][0]);
    }
    const totalTimeMinutes = Math.max((totalDistance / 40) * 60, totalDistance * 2);
    const now = new Date();
    let cumulativeTime = 0;
    const routeWithArrivals = newOptimizedRoute.map(item => {
      const legTime = (item.distance || 0) * 1.5;
      cumulativeTime += legTime;
      return {...item, arrivalTime: new Date(now.getTime() + cumulativeTime * 60 * 1000)};
    });
    setOptimizedRoute(routeWithArrivals);
    setRouteInfo({ totalDistance, totalTime: totalTimeMinutes });
  };

  const resetBottomSheet = () => { setBottomSheetContent('inspections'); setBottomSheetHeight('50%'); };
  const handleInspectionSelect = (id: string) => {
    setSelectedInspections((prev) => {
      const newSelected = prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id];
      calculateTotalPoints(newSelected);
      return newSelected;
    });
  };
  const calculateTotalPoints = (selectedIds: string[]) => {
    let total = 0;
    selectedIds.forEach((id) => {
      const inspection = establishments.find((insp) => String(insp.id) === id);
      if (inspection) total += 1.1;
    });
    setTotalSelectedPoints(total);
  };

  const handleUnassignTask = async (taskId: string): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsLoading(true);
        const response = await fetch(`${BASE_URL}/sdp/rules/api/v2/task/actions/unassign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth?.user?.access_token}` },
          body: JSON.stringify({ task_id: taskId }),
        });
        if (!response.ok) throw new Error(`Failed to unassign task: ${response.status}`);
        await fetchEstablishmentsData();
        resolve();
      } catch (error) {
        console.error('Error unassigning task:', error);
        reject(error);
      } finally {
        setIsLoading(false);
      }
    });
  };

  const handleBulkAssign = () => { if (selectedInspections.length > 0) setShowBulkAssignDialog(true); };
  const confirmBulkAssignment = async () => {
    setIsBulkAssigning(true);
    setShowBulkAssignDialog(false);
    const total = selectedInspections.length;
    let success = 0;
    let failed = 0;
    setBulkAssignResults({ success: 0, failed: 0, total });
    const currentToken = auth?.user?.access_token;
    if (!currentToken) {
      setBulkAssignToastType('error');
      setBulkAssignToastMessage(t('errors.notAuthenticated'));
      setShowBulkAssignToast(true);
      setIsBulkAssigning(false);
      return;
    }
    for (const inspectionId of selectedInspections) {
      try {
        const response = await fetch(`${BASE_URL}/sdp/rules/api/v2/task/actions/self_assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${currentToken}` },
          body: JSON.stringify({ task_id: inspectionId }),
        });
        const data = await response.json();
        if (response.ok && data.status === true) success++;
        else {
          failed++;
          console.error('Failed to assign task:', inspectionId, data);
        }
      } catch (error) {
        failed++;
        console.error('Error assigning task:', inspectionId, error);
      }
      setBulkAssignResults({ success, failed, total });
    }
    if (failed === 0) {
      setBulkAssignToastType('success');
      setBulkAssignToastMessage(t('common.bulkAssignSuccess', { count: success }));
    } else if (success === 0) {
      setBulkAssignToastType('error');
      setBulkAssignToastMessage(t('common.bulkAssignFailed', { count: failed }));
    } else {
      setBulkAssignToastType('success');
      setBulkAssignToastMessage(t('common.bulkAssignPartial', { success, failed }));
    }
    setShowBulkAssignToast(true);
    setIsBulkAssigning(false);
    setSelectedInspections([]);
    setTotalSelectedPoints(0);
    if (success > 0) {
      window.dispatchEvent(new CustomEvent('inspection:bulk-assigned', { detail: { count: success } }));
      setShowCountdown(true);
      setCountdownSeconds(30);
      const countdownInterval = setInterval(() => {
        setCountdownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setShowCountdown(false);
            fetchEstablishmentsData();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      fetchEstablishmentsData();
    }
  };

  const getRiskColorHex = (grade: string): string => {
    switch (grade?.toUpperCase()) {
      case 'LOW': return '#00843d';
      case 'MEDIUM': return '#f97316';
      case 'HIGH': return '#c8102e';
      default: return '#b89256';
    }
  };

  const BulkAssignmentDialog = () => {
    if (!showBulkAssignDialog) return null;
    return (
      <div className="fixed inset-0 bg-[#00000087] flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
          <h3 className="text-lg font-semibold mb-2">{t('common.confirmBulkAssignment')}</h3>
          <p className="text-gray-600 mb-4">
            {t('common.bulkAssignmentConfirmMessage', { count: selectedInspections.length })}
          </p>
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowBulkAssignDialog(false)} className="border-gray-300">
              <X className="w-4 h-4 mr-2" />{t('common.cancel')}
            </Button>
            <Button className="bg-[#b89256] text-white hover:opacity-90" onClick={confirmBulkAssignment}>
              <Check className="w-4 h-4 mr-2" />{t('common.confirm')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const BulkAssignToast = () => {
    if (!showBulkAssignToast) return null;
    return createPortal(
      <div className="fixed bottom-4 right-2 z-[9999]">
        <div className={`rounded-md shadow-md p-4 flex items-center ${
          bulkAssignToastType === 'success' ? 'bg-[#b89256] border-l-4 border-[#b89256]' : 'bg-red-50 border-l-4 border-red-500'
        }`}>
          <div className={`mr-3 ${bulkAssignToastType === 'success' ? 'text-white' : 'text-red-500'}`}>
            {bulkAssignToastType === 'success' ? <Check className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <p className={`text-sm font-medium ${
              bulkAssignToastType === 'success' ? 'text-white' : 'text-red-800'
            }`}>
              {bulkAssignToastMessage}
            </p>
          </div>
          <button onClick={() => setShowBulkAssignToast(false)} className="ml-4 text-gray-400 hover:text-gray-500">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="flex flex-col min-h-screen" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <Header title={language === 'en' ? 'UrbiMap Inspections' : 'تفتيشات UrbiMap'} isRTL={language === 'ar'} showBackButton={false} backTo="/plan" />
      <div className="relative h-screen overflow-hidden touch-none">
        {/* --- Map Container --- */}
        <div ref={mapContainer} style={mapContainerStyle} />

        {/* --- Loading/Error Overlays --- */}
        {(mapLoading || isLoading) && !mapError && (
          <div className="absolute inset-0 bg-gray-100/80 flex items-center justify-center z-40">
            <div className="text-center p-4">
              <div className="w-12 h-12 border-4 border-amber-700 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                {isLoading ? t('common.loading') : t('map.loadingMap')}
              </h3>
              <p className="text-sm text-gray-500">{t('map.pleaseWait')}</p>
            </div>
          </div>
        )}
        {error && !mapError && !isLoading && (
          <div className="absolute top-20 left-0 right-0 mx-auto w-max bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              <span>{error}</span>
              <Button variant="ghost" size="sm" className="ml-4 text-xs" onClick={() => fetchEstablishmentsData()}>
                {t('common.retry')}
              </Button>
            </div>
          </div>
        )}
        {mapError && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-100">
            <div className="text-center p-4 bg-white rounded shadow-md">
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-red-700 font-medium">Error loading map.</p>
              <p className="text-sm text-gray-600 mb-3">Please check the API key and network connection.</p>
              <Button onClick={() => window.location.reload()}>Retry Page</Button>
            </div>
          </div>
        )}

        {/* --- UI Elements (Search, Filters, Controls, Bottom Sheet) --- */}
        <div className="absolute top-4 left-0 right-0 mx-auto w-[90%] z-30">
          <div className="relative">
            <div className="bg-white rounded-lg shadow-lg flex items-center h-10 px-4 max-w-md mx-auto">
              <Search className="w-5 h-5 text-gray-500 mr-2" />
              <Input
                placeholder={t('plan.searchEstablishments')}
                className="border-0 h-full focus-visible:ring-0 focus-visible:ring-offset-0 placeholder-gray-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => setSearchQuery('')}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <div className="mt-1 flex gap-1 items-center overflow-x-auto py-2 px-0 w-full hide-webkit-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            {filterButtons.map((button) => (
              <Button
                key={button.id}
                variant="ghost"
                size="sm"
                className={`rounded-lg flex items-center justify-center gap-1 px-3 py-1 ${
                  (button.contentType ? bottomSheetContent === button.contentType : activeFilter === button.filterValue)
                    ? 'bg-[#b89256] text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-200 shadow-sm'
                }`}
                onClick={() => {
                  if (activeFilter === button.filterValue && button.action === 'setFilter') return;
                  if ((button.filterValue !== 'adhoc' && button.filterValue !== 'inProgress' && button.filterValue !== 'paused' && button.filterValue !== 'completed') && isAdhoc) {
                    showToast('error', t('filters.adhocFilterError'));
                    return;
                  }
                  if (button.action === 'showSuggestions') showSuggestions();
                  else if (button.action === 'showRouteOptimization') showRouteOptimization();
                  else if (button.action === 'setFilter' && button.filterValue) {
                    setBottomSheetContent('inspections');
                    setIsBottomSheetExpanded(true);
                    setActiveFilter(button.filterValue);
                  }
                  if (button.filterValue && button.action !== 'showSuggestions' && button.action !== 'showRouteOptimization') {
                    setActiveFilter(button.filterValue);
                  }
                }}
              >
                <div className="flex items-center gap-1">
                  {button.icon}
                  <span className="text-xs font-medium">{t(`filters.${button.id}`)}</span>
                  <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-semibold rounded-full ${
                    (button.contentType ? bottomSheetContent === button.contentType : activeFilter === button.filterValue)
                      ? 'bg-white text-[#b89256]'
                      : 'bg-[#b89256] text-white'
                  }`}>
                    {button.count > 99 ? '99+' : button.count}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </div>

        <div className={`absolute right-2 z-30 flex flex-col gap-2 right-[1rem] ${isBottomSheetExpanded ? 'top-[20%]' : 'bottom-[7rem]'}`}>
          <div className="mb-2">
            <Button
              variant="ghost"
              size="icon"
              className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center"
              onClick={() => {
                if (mapRef.current && establishments.length > 0) {
                  console.log("2GIS MapGL: Fitting bounds to establishments");
                }
              }}
              title={t('map.fitToScreen') || 'Fit to Screen'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4H9M4 4V9M4 4L9 9M20 4H15M20 4V9M20 4L15 9M4 20H9M4 20V15M4 20L9 15M20 20H15M20 20V15M20 20L15 15" stroke="#b89256" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Button>
          </div>
          {activeFilter === 'assigned' && (
            <div className="mb-2">
              <Button
                variant="ghost"
                size="icon"
                className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center"
                onClick={showRouteOptimization}
                title={t('plan.routeOptimization') || 'Route Optimization'}
              >
                <Route className="w-5 h-5 text-[#b89256]" />
              </Button>
            </div>
          )}
          <div className="mb-2">
            <Button
              variant="ghost"
              size="icon"
              className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center"
              onClick={() => {
                if (activeFilter && isAdhoc) {
                  showToast('error', t('filters.adhocFilterError'));
                  return;
                } else {
                  showSuggestions();
                }
              }}
              title={t('plan.suggestedInspections') || 'Suggested Inspections'}
            >
              <Sparkles className="w-5 h-5 text-[#b89256]" />
            </Button>
          </div>
        </div>

        <div
          className={`absolute bottom-0 left-0 right-0 bg-white shadow-xl z-20 transition-all duration-300 ease-in-out`}
          style={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px', overflowY: isBottomSheetExpanded ? 'auto' : 'hidden', overflowX: 'hidden', height: isBottomSheetExpanded ? bottomSheetHeight : '100px' }}
        >
          <div className="w-full flex justify-center items-center pt-3 pb-2 cursor-pointer" onClick={toggleBottomSheet}>
            <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
          </div>
          {!isBottomSheetExpanded && (
            <div className="px-4 py-2">
              <h2 className="text-lg font-medium">
                {selectedEstablishment
                  ? establishments.find((e) => groupedEstablishments.find(g => g.id === selectedEstablishment)?.establishment_id === e.establishment_id)?.establishment.name_en
                  : t('plan.inspections')}
              </h2>
              <div className="flex mt-1">
                <div className="flex items-center text-sm text-gray-500">
                  <div className="w-6 h-6 rounded-full bg-[#b89256] flex items-center justify-center mr-1"><MapPin className="w-3 h-3 text-white" /></div>
                  <span>
                    {selectedEstablishment
                      ? establishments.find((e) => groupedEstablishments.find(g => g.id === selectedEstablishment)?.establishment_id === e.establishment_id)?.emirate.name_en
                      : t('plan.selectLocation')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {isBottomSheetExpanded && (
            <div className="px-4 pb-20">
              {bottomSheetContent === 'inspections' && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{t('plan.inspections')}</h2>
                  </div>
                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-[200px]">
                      <div className="w-12 h-12 border-4 border-[#b89256] border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-gray-600 font-medium">{t('common.loading')}</p>
                    </div>
                  ) : (
                    <div className="w-full overflow-y-auto">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {groupedEstablishments.map((item) => (
                          <div
                            key={item.id}
                            className={`cursor-pointer`}
                            onClick={() => {
                              setSelectedEstablishment(String(item.id));
                              if (mapRef.current) {
                                mapRef.current.setCenter([item.location.lng, item.location.lat], { animate: true, duration: 300 });
                                mapRef.current.setZoom(15, { animate: true, duration: 300 });
                              }
                            }}
                          >
                            <InspectionCard
                              id={String(item.id)}
                              establishment_id={item.establishment_id}
                              establishment={item.establishment}
                              emirate={item.emirate}
                              inspection_type={item.inspection_type}
                              risk_grade={item.risk_grade}
                              risk_category={item.risk_category}
                              status={item.status}
                              language={language}
                              points={item.groupCount * 1.1}
                              groupCount={item.groupCount}
                              assigned_by={activeFilter === 'assigned' && item.groupedItems?.some(gi => gi.assigned_by === 'self') ? 'self' : undefined}
                              onUnassign={activeFilter === 'assigned' ? async () => {
                                if (item.groupedItems) {
                                  const selfAssigned = item.groupedItems.filter(gi => gi.assigned_by === 'self');
                                  if (selfAssigned.length > 0) await Promise.all(selfAssigned.map(gi => handleUnassignTask(String(gi.id))));
                                  else console.warn("No self-assigned tasks");
                                }
                              } : undefined}
                              onSelect={activeFilter === 'pool' ? () => {
                                const groupTaskIds = item.groupedItems?.map(gi => String(gi.id)) || [];
                                const currentlySelected = groupTaskIds.every(id => selectedInspections.includes(id));
                                if (currentlySelected) setSelectedInspections(prev => prev.filter(id => !groupTaskIds.includes(id)));
                                else setSelectedInspections(prev => [...new Set([...prev, ...groupTaskIds])]);
                              } : undefined}
                              isSelected={item.groupedItems?.every(gi => selectedInspections.includes(String(gi.id)))}
                            />
                          </div>
                        ))}
                        {groupedEstablishments.length === 0 && (
                          <div className="text-center py-8 col-span-full">
                            <Info className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-600">{t('plan.noEstablishmentsFound')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {activeFilter === 'pool' && selectedInspections.length > 0 && (
                    <div className="fixed bottom-4 left-4 right-4 z-40 flex justify-center">
                      <Button
                        className="bg-[#b89256] text-white shadow-lg rounded-full px-6 py-3 flex items-center gap-2 hover:opacity-90"
                        onClick={handleBulkAssign}
                        disabled={isBulkAssigning}
                      >
                        {isBulkAssigning ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <UserPlus className="w-4 h-4" />
                        )}
                        {t('common.assignSelected', { count: selectedInspections.length })} ({totalSelectedPoints.toFixed(1)} pts)
                      </Button>
                    </div>
                  )}
                </>
              )}
              {bottomSheetContent === 'suggestions' && (
                <div className="h-full">
                  <h2 className="text-xl font-bold mb-4">{t('plan.suggestedInspections')}</h2>
                  <div className="w-full overflow-y-auto">
                    <SuggestedInspectionsPage />
                  </div>
                </div>
              )}
              {bottomSheetContent === 'routes' && (
                <div className="h-full">
                  <h2 className="text-xl font-bold mb-4">{t('plan.routeOptimization')}</h2>
                  <div className="w-full overflow-y-auto">
                    {routeInfo && (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-lg font-semibold text-gray-800">Route Summary</h3>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="text-xs border border-[#b89256]" onClick={recalculateRoute}>
                              Recalculate Route
                            </Button>
                            {!navigationActive ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs bg-[#b89256] text-white hover:bg-[#b89256] border-none"
                                onClick={() => {
                                  setNavigationActive(true);
                                  setCurrentStepIndex(0);
                                }}
                              >
                                <Navigation className="w-3 h-3 mr-1" />Start My Trip
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs bg-red-600 text-white hover:bg-red-700 border-none"
                                onClick={() => {
                                  setNavigationActive(false);
                                }}
                              >
                                <X className="w-3 h-3 mr-1" />Exit Navigation
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 p-3 rounded-md">
                            <p className="text-sm text-gray-500">Total Distance</p>
                            <p className="text-lg font-bold">{routeInfo.totalDistance.toFixed(1)} km</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md">
                            <p className="text-sm text-gray-500">Estimated Time</p>
                            <p className="text-lg font-bold">
                              {routeInfo.totalTime >= 60
                                ? `${Math.floor(routeInfo.totalTime / 60)}h ${Math.round(routeInfo.totalTime % 60)}m`
                                : `${Math.round(routeInfo.totalTime)}m`}
                            </p>
                          </div>
                        </div>
                        {navigationActive && navigationSteps.length > 0 && (
                          <div className="mt-4 border-t pt-3">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Turn-by-Turn Navigation (Placeholder)</h4>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="space-y-4 mt-4">
                      {optimizedRoute.map((item, index) => (
                        <div key={item.id} className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#b89256] text-white flex items-center justify-center font-bold shadow-md z-10">{index + 1}</div>
                            {item.arrivalTime && (
                              <div className="text-sm font-medium text-gray-700">
                                ETA: {item.arrivalTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                {' '}({item.distance ? `${item.distance.toFixed(1)}km` : ''})
                              </div>
                            )}
                          </div>
                          <div className="">
                            <InspectionCard
                              id={String(item.id)}
                              establishment_id={item.establishment_id}
                              establishment={item.establishment}
                              emirate={item.emirate}
                              inspection_type={item.inspection_type}
                              risk_grade={item.risk_grade}
                              risk_category={item.risk_category}
                              status={item.status}
                              language={language}
                              points={item.groupCount ? item.groupCount * 1.1 : 1.1}
                              groupCount={item.groupCount}
                              distance={item.distance}
                              assigned_by={item.assigned_by}
                              onUnassign={undefined}
                            />
                            {item.distance !== undefined && index > 0 && (
                              <div className="mt-1 ml-10 text-sm text-gray-500">
                                <span className="flex items-center"><Navigation className="w-3 h-3 mr-1" />{item.distance.toFixed(1)} km from previous</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {optimizedRoute.length === 0 && !isLoading && (
                        <div className="text-center py-8">
                          <Route className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600">No assigned establishments to optimize route</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <BulkAssignmentDialog />
        <BulkAssignToast />
      </div>
    </div>
  );
}